<lib-not-modal [width]="'min(720px, 100dvw)'">
  <div class="client-item-detail-modal">
    <div class="client-item-detail-modal__intro">
      <h3
        class="client-item-detail-modal__title"
        [attr.title]="item().name"
      >
        {{ item().name }}
        @if (quantityBadge(); as badge) {
          <span class="staff-user-item-detail-modal__quantity-badge">{{
            badge
          }}</span>
        }
      </h3>
      <div class="client-item-detail-modal__intro-meta">
        @if (item().client_deleted_at) {
          <span class="staff-user-item-detail-modal__badge">
            {{ 'admin.users.items.clientDeletedBadge' | translate }}
          </span>
        }
        <span
          class="client-item-detail-modal__status"
          [class]="statusBadgeClass()"
        >
          {{ statusLabelKey() | translate }}
        </span>
      </div>
    </div>

    <div class="client-item-detail-modal__body">
      <div
        class="client-item-detail-modal__gallery"
        role="group"
        [attr.aria-label]="'CLIENT.CABINET.MEDIA.ITEM_LIST_LABEL' | translate"
      >
        @if (editMode()) {
          <lib-cabinet-photo-uploader
            entityType="item"
            [entityId]="item().id"
            [maxFiles]="maxPhotos"
            accept="image/jpeg,image/png,image/webp"
            listAriaLabel="CLIENT.CABINET.MEDIA.ITEM_LIST_LABEL"
            emptyTitleKey="CLIENT.CABINET.MEDIA.ITEM_EMPTY_TITLE"
            emptyHintKey="CLIENT.CABINET.MEDIA.STAFF_ITEM_EMPTY_HINT"
          />
        } @else {
        @if (photosLoading()) {
          <div class="client-item-detail-modal__preview">
            <div class="client-item-detail-modal__preview-placeholder">
              {{ 'CLIENT.CABINET.ITEM_CATALOG.LOADING' | translate }}
            </div>
          </div>
        } @else if (hasPhotos()) {
          <lib-cabinet-item-photo-preview
            [src]="activePhotoUrl()"
            [alt]="item().name"
          >
            @if (hasMultiplePhotos()) {
              <button
                type="button"
                class="client-item-detail-modal__nav client-item-detail-modal__nav--prev"
                [attr.aria-label]="'A11Y.PREVIOUS_SLIDE' | translate"
                (click)="showPreviousPhoto()"
              >
                <svg
                  class="client-item-detail-modal__nav-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M14.5 6.5 9 12l5.5 5.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                class="client-item-detail-modal__nav client-item-detail-modal__nav--next"
                [attr.aria-label]="'A11Y.NEXT_SLIDE' | translate"
                (click)="showNextPhoto()"
              >
                <svg
                  class="client-item-detail-modal__nav-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M9.5 6.5 15 12l-5.5 5.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            }
          </lib-cabinet-item-photo-preview>
        } @else {
          <div class="client-item-detail-modal__preview">
            <div
              class="client-item-detail-modal__preview-placeholder"
              role="img"
              [attr.aria-label]="'CLIENT.CABINET.ITEM_CATALOG.NO_PHOTO' | translate"
            >
              <img
                class="client-item-detail-modal__preview-placeholder-image"
                src="/icons/core/no-photo.svg"
                alt=""
                aria-hidden="true"
                width="80"
                height="80"
                decoding="async"
              />
            </div>
          </div>
        }

        @if (hasMultiplePhotos()) {
          <div class="client-item-detail-modal__gallery-footer">
            <div
              class="client-item-detail-modal__dots"
              role="tablist"
              [attr.aria-label]="'CLIENT.CABINET.MEDIA.ITEM_LIST_LABEL' | translate"
            >
              @for (photo of photos(); track photo; let index = $index) {
                <button
                  type="button"
                  class="client-item-detail-modal__dot"
                  role="tab"
                  [class.client-item-detail-modal__dot--active]="
                    activePhotoIndex() === index
                  "
                  [attr.aria-selected]="activePhotoIndex() === index"
                  [attr.aria-label]="
                    'CLIENT.CABINET.ITEM_CATALOG.PHOTO_N'
                      | translate : { n: index + 1 }
                  "
                  (click)="goToPhoto(index)"
                ></button>
              }
            </div>
            <span class="client-item-detail-modal__counter" aria-live="polite">
              {{ photoCounter() }}
            </span>
          </div>
        }
        }
      </div>

      <div class="client-item-detail-modal__info">
        @if (editMode()) {
          <form
            class="staff-user-item-detail-modal__form"
            [formGroup]="form"
            (ngSubmit)="onSaveItem()"
          >
            @if (canEditAllFields()) {
              <lib-not-input
                type="text"
                formControlName="name"
                [label]="'CLIENT.CABINET.ITEM_CATALOG.FIELD_NAME' | translate"
                [required]="true"
              />
              <lib-not-input
                type="textarea"
                formControlName="description"
                [label]="
                  'CLIENT.CABINET.ITEM_CATALOG.FIELD_DESCRIPTION' | translate
                "
              />
              <lib-client-item-tags-control
                [(tags)]="itemTags"
                [suggestions]="data.tagSuggestions ?? []"
              />
              <lib-not-input
                type="number"
                formControlName="quantity"
                [label]="'CLIENT.CABINET.ITEM_CATALOG.FIELD_QUANTITY' | translate"
                [placeholder]="
                  'CLIENT.CABINET.ITEM_CATALOG.FIELD_QUANTITY_PLACEHOLDER'
                    | translate
                "
                [hideErrorBlock]="true"
              />

              @if (showDimensionsScopeToggle()) {
                <div
                  class="staff-user-item-detail-modal__scope"
                  role="group"
                  [attr.aria-label]="
                    'CLIENT.CABINET.ITEM_CATALOG.FIELD_DIMENSIONS_SCOPE'
                      | translate
                  "
                >
                  <span class="staff-user-item-detail-modal__scope-label">
                    {{
                      'CLIENT.CABINET.ITEM_CATALOG.FIELD_DIMENSIONS_SCOPE'
                        | translate
                    }}
                  </span>
                  <div class="staff-user-item-detail-modal__scope-toggle">
                    <button
                      type="button"
                      class="staff-user-item-detail-modal__scope-option"
                      [class.staff-user-item-detail-modal__scope-option--active]="
                        form.controls.dimensions_scope.value === 'per_unit'
                      "
                      [attr.aria-pressed]="
                        form.controls.dimensions_scope.value === 'per_unit'
                      "
                      (click)="setDimensionsScope('per_unit')"
                    >
                      {{
                        'CLIENT.CABINET.ITEM_CATALOG.DIMENSIONS_SCOPE_PER_UNIT'
                          | translate
                      }}
                    </button>
                    <button
                      type="button"
                      class="staff-user-item-detail-modal__scope-option"
                      [class.staff-user-item-detail-modal__scope-option--active]="
                        form.controls.dimensions_scope.value === 'total'
                      "
                      [attr.aria-pressed]="
                        form.controls.dimensions_scope.value === 'total'
                      "
                      (click)="setDimensionsScope('total')"
                    >
                      {{
                        'CLIENT.CABINET.ITEM_CATALOG.DIMENSIONS_SCOPE_TOTAL'
                          | translate
                      }}
                    </button>
                  </div>
                </div>
              }

              @if (dimensionsHintKey(); as hintKey) {
                <p class="staff-user-item-detail-modal__hint">
                  {{ hintKey | translate }}
                </p>
              }

              @if (scopeChanged()) {
                <p class="staff-user-item-detail-modal__hint staff-user-item-detail-modal__hint--warning">
                  {{
                    'CLIENT.CABINET.ITEM_CATALOG.DIMENSIONS_SCOPE_CHANGE_WARNING'
                      | translate
                  }}
                </p>
              }

              <div class="staff-user-item-detail-modal__dims">
                <lib-not-input
                  type="text"
                  formControlName="width_cm"
                  [label]="'CLIENT.CABINET.ITEM_CATALOG.FIELD_WIDTH' | translate"
                  [hideErrorBlock]="true"
                />
                <lib-not-input
                  type="text"
                  formControlName="height_cm"
                  [label]="'CLIENT.CABINET.ITEM_CATALOG.FIELD_HEIGHT' | translate"
                  [hideErrorBlock]="true"
                />
                <lib-not-input
                  type="text"
                  formControlName="depth_cm"
                  [label]="'CLIENT.CABINET.ITEM_CATALOG.FIELD_DEPTH' | translate"
                  [hideErrorBlock]="true"
                />
                <lib-not-input
                  type="text"
                  formControlName="weight_kg"
                  [label]="'CLIENT.CABINET.ITEM_CATALOG.FIELD_WEIGHT' | translate"
                  [hideErrorBlock]="true"
                />
              </div>

              @if (previewVolumeLabel(); as volume) {
                <p class="staff-user-item-detail-modal__volume-preview">
                  {{
                    'CLIENT.CABINET.ITEM_CATALOG.VOLUME_PREVIEW'
                      | translate : { volume }
                  }}
                </p>
              }
            } @else {
              <p class="staff-user-item-detail-modal__hint">
                {{ 'admin.storageRequests.orderItemsLimitedEditHint' | translate }}
              </p>
            }

            <label class="staff-user-item-detail-modal__toggle">
              <input type="checkbox" formControlName="is_fragile" />
              <span>{{ fragileLabelKey() | translate }}</span>
            </label>

            <label class="staff-user-item-detail-modal__toggle">
              <input type="checkbox" formControlName="is_sentimental" />
              <span>{{
                'CLIENT.CABINET.ITEM_CATALOG.FIELD_SENTIMENTAL' | translate
              }}</span>
            </label>

            <label class="staff-user-item-detail-modal__toggle">
              <input
                type="checkbox"
                formControlName="is_disassembled"
                (change)="onDisassembledToggle()"
              />
              <span>{{
                'CLIENT.CABINET.ITEM_CATALOG.FIELD_DISASSEMBLED' | translate
              }}</span>
            </label>

            @if (form.controls.is_disassembled.value) {
              <lib-not-input
                type="number"
                formControlName="parts_count"
                [label]="'CLIENT.CABINET.ITEM_CATALOG.FIELD_PARTS_COUNT' | translate"
                [required]="true"
                [hideErrorBlock]="true"
              />
            }

            <div class="staff-user-item-detail-modal__actions">
              <lib-not-button
                type="button"
                size="big"
                [text]="'common.actions.cancel' | translate"
                [bgColor]="'rgba(55, 190, 176, 0.12)'"
                [color]="'#140342'"
                [width]="'100%'"
                [action]="{ type: 'action' }"
                [disabled]="savingItem()"
                (actionEvent)="toggleEditMode()"
              />
              <lib-cabinet-button
                type="submit"
                variant="primary"
                size="lg"
                [fullWidth]="true"
                [text]="'common.actions.update' | translate"
                [action]="{ type: 'action' }"
                [disabled]="savingItem()"
                [isLoading]="savingItem()"
                (actionEvent)="onSaveItem()"
              />
            </div>
          </form>
        } @else {
          @if (item().tags?.length) {
            <lib-client-item-tags-chips [tags]="item().tags ?? []" />
          }

          @if (item().is_sentimental) {
            <p class="staff-user-item-detail-modal__sentimental">
              {{ 'CLIENT.CABINET.ITEM_CATALOG.FIELD_SENTIMENTAL' | translate }}
            </p>
          }

          @if (item().is_fragile) {
            <p class="staff-user-item-detail-modal__fragile">
              {{ fragileLabelKey() | translate }}
            </p>
          }

          @if (item().is_disassembled && item().parts_count) {
            <p class="staff-user-item-detail-modal__disassembled">
              {{
                'CLIENT.CABINET.ITEM_CATALOG.DISASSEMBLED_DETAIL'
                  | translate : { count: item().parts_count }
              }}
            </p>
          }

          @if (dimensionsLabel(); as dimensions) {
            <p class="client-item-detail-modal__dimensions">{{ dimensions }}</p>
          }

          @if (itemPlacementAddresses(item()); as placement) {
            <p class="client-item-detail-modal__placement">
              <span class="client-item-detail-modal__placement-label">
                {{ itemPlacementLabelKey(item()) | translate }}:
              </span>
              {{ placement }}
            </p>
          }

          @if (item().description; as description) {
            <p class="client-item-detail-modal__description">{{ description }}</p>
          } @else {
            <p
              class="client-item-detail-modal__description client-item-detail-modal__description--empty"
            >
              {{ 'CLIENT.CABINET.ITEM_CATALOG.NO_DESCRIPTION' | translate }}
            </p>
          }
        }

        @if (item().client_deleted_at) {
          <p class="staff-user-item-detail-modal__hint staff-user-item-detail-modal__hint--muted">
            {{ 'admin.users.items.clientDeletedHint' | translate }}
          </p>
        } @else if (item().in_active_order && item().status === 'pending') {
          <p class="staff-user-item-detail-modal__hint staff-user-item-detail-modal__hint--warning">
            {{ 'admin.users.items.pendingBlockedHint' | translate }}
          </p>
        } @else if (item().in_active_order) {
          <p class="staff-user-item-detail-modal__hint staff-user-item-detail-modal__hint--warning">
            {{ 'admin.users.items.inActiveOrderHint' | translate }}
          </p>
        }

        @if (canManageCellPlacement() && !resolvedWarehouseId()) {
          <p class="staff-user-item-detail-modal__hint staff-user-item-detail-modal__hint--warning">
            {{ 'admin.users.items.warehouseRequiredHint' | translate }}
          </p>
        }

        @if (placementPanelOpen()) {
          <section class="staff-user-item-detail-modal__placement-panel">
            <h4 class="staff-user-item-detail-modal__placement-title">
              {{ 'admin.users.items.placementPanelTitle' | translate }}
            </h4>
            @if (resolvedWarehouseId(); as warehouseId) {
              <lib-staff-warehouse-cell-picker
                [warehouseId]="warehouseId"
                [initialCells]="placementInitialCells()"
                [submitLabelKey]="placementSubmitLabelKey()"
                [submitting]="placementSubmitting()"
                [showCancel]="true"
                (submitCells)="onPlacementSubmit($event)"
                (cancelled)="closePlacementPanel()"
              />
            }
          </section>
        }

        <div class="client-item-detail-modal__actions staff-user-item-detail-modal__actions">
          @if ((editable() || deletable()) && !editMode()) {
            <div class="staff-user-item-detail-modal__primary-actions">
              @if (editable()) {
                <lib-not-button
                  type="button"
                  size="big"
                  [text]="'admin.storageRequests.orderItemsEditItem' | translate"
                  [bgColor]="'rgba(55, 190, 176, 0.12)'"
                  [color]="'#140342'"
                  [width]="'100%'"
                  [action]="{ type: 'action' }"
                  (actionEvent)="toggleEditMode()"
                />
              }
              @if (deletable()) {
                <lib-not-button
                  type="button"
                  size="big"
                  [text]="'admin.users.items.delete' | translate"
                  [disabled]="deletingItem() || updating()"
                  [bgColor]="'rgba(244, 143, 177, 0.16)'"
                  [color]="'#C2185B'"
                  [width]="'100%'"
                  [action]="{ type: 'action' }"
                  (actionEvent)="onDeleteItem()"
                />
              }
            </div>
          }

          @if (!editMode()) {
          <lib-not-button
            type="button"
            size="big"
            [text]="'admin.users.items.printQr' | translate"
            [disabled]="printingQr() || updating()"
            [bgColor]="'rgba(55, 190, 176, 0.12)'"
            [color]="'#140342'"
            [width]="'100%'"
            [action]="{ type: 'action' }"
            (actionEvent)="onPrintQr()"
          />

          @if (showPlaceInCellButton()) {
            <lib-not-button
              type="button"
              size="big"
              [text]="'admin.users.items.placeInCell' | translate"
              [disabled]="placementWarehouseActionsDisabled()"
              [bgColor]="'#37BEB0'"
              [color]="'#FFFFFF'"
              [width]="'100%'"
              [action]="{ type: 'action' }"
              (actionEvent)="openPlacementPanel('place')"
            />
          }

          @if (showMoveAndClearButtons()) {
            <lib-not-button
              type="button"
              size="big"
              [text]="'admin.users.items.moveCell' | translate"
              [disabled]="placementWarehouseActionsDisabled()"
              [bgColor]="'#37BEB0'"
              [color]="'#FFFFFF'"
              [width]="'100%'"
              [action]="{ type: 'action' }"
              (actionEvent)="openPlacementPanel('move')"
            />
            <lib-not-button
              type="button"
              size="big"
              [text]="'admin.users.items.clearFromCell' | translate"
              [disabled]="clearingPlacementDisabled()"
              [bgColor]="'rgba(55, 190, 176, 0.12)'"
              [color]="'#140342'"
              [width]="'100%'"
              [action]="{ type: 'action' }"
              (actionEvent)="onClearPlacement()"
            />
          }

          @if (allowedNextStatuses().length > 0) {
            @for (nextStatus of allowedNextStatuses(); track nextStatus) {
              <lib-not-button
                type="button"
                size="big"
                [text]="
                  nextStatus === 'at_client'
                    ? ('admin.users.items.actions.revertToAtClient' | translate)
                    : ('admin.users.items.actions.changeTo'
                      | translate
                        : {
                            status: (itemStatusLabelKey(nextStatus) | translate),
                          })
                "
                [disabled]="updating()"
                [bgColor]="nextStatus === 'at_client' ? 'rgba(55, 190, 176, 0.12)' : '#37BEB0'"
                [color]="nextStatus === 'at_client' ? '#140342' : '#FFFFFF'"
                [width]="'100%'"
                [action]="{ type: 'action' }"
                (actionEvent)="onChangeStatus(nextStatus)"
              />
            }
          }
          }
        </div>
      </div>
    </div>
  </div>
</lib-not-modal>
